import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ArrowUpRight, Sparkles, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import TritalLogo from "../TritalLogo";
import { SHIFTS_CTAS, MARKETPLACE_BRAND } from "../../data/marketplaceData";
import ShiftsFooter from "./ShiftsFooter";

/* ------------------------------------------------------------------ */
/*  Marketplace shell — distinct from the main marketing PublicNav    */
/*  Used as a wrapper component (children = page content).            */
/* ------------------------------------------------------------------ */

const NAV = [
  { to: "/shiftbridge",            label: "Home" },
  { to: "/shiftbridge/browse",     label: "Browse Shifts" },
  { to: "/shiftbridge/facilities", label: "For Facilities" },
  { to: "/shiftbridge/clinicians", label: "For Clinicians" },
  { to: "/shiftbridge/faq",        label: "FAQ" },
];

export default function ShiftsLayout({ children }) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-white" data-testid="shifts-app">
      {/* Top bar — back-to-main + brand line */}
      <div className="hidden md:block bg-trital-navy-ink text-white/80 border-b border-white/8" data-testid="shifts-utility-bar">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-9 flex items-center justify-between text-[12px]">
          <Link to="/" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="shifts-back-main">
            <ArrowUpRight className="w-3 h-3 rotate-180" strokeWidth={1.8} />
            tritalcare.com — full company site
          </Link>
          <div className="flex items-center gap-5">
            <a href="mailto:shifts@tritalcare.com" className="hover:text-white" data-testid="shifts-utility-support">shifts@tritalcare.com</a>
            <span className="text-white/20">·</span>
            <Link to="/compliance" className="hover:text-white">Compliance</Link>
          </div>
        </div>
      </div>

      {/* Main marketplace bar */}
      <header
        className={`sticky top-0 z-40 bg-white/92 backdrop-blur-xl border-b border-slate-200 transition-shadow ${scrolled ? "shadow-[0_4px_14px_-4px_rgba(15,23,42,0.08)]" : ""}`}
        data-testid="shifts-nav"
      >
        <div className={`max-w-[1400px] mx-auto px-5 lg:px-8 flex items-center gap-4 transition-[height] ${scrolled ? "h-[64px]" : "h-[76px]"}`}>
          {/* Brand */}
          <Link to="/shiftbridge" className="shrink-0 flex items-center" data-testid="shifts-brand">
            <TritalLogo platform="shiftbridge" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 ml-6" aria-label="Marketplace">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/shiftbridge"}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-[13.5px] font-semibold transition-colors ${
                    isActive ? "text-trital-navy bg-slate-100" : "text-slate-700 hover:text-trital-navy"
                  }`
                }
                data-testid={`shifts-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Right CTA cluster */}
          <div className="hidden lg:flex items-center gap-2 ml-auto">
            {user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="shifts-nav-dashboard"
              >
                Open dashboard <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            ) : (
              <>
                <Link
                  to={SHIFTS_CTAS.login}
                  className="px-3 py-2 text-[13.5px] font-semibold text-slate-700 hover:text-trital-navy"
                  data-testid="shifts-nav-login"
                >
                  Sign in
                </Link>
                <Link
                  to={SHIFTS_CTAS.facilitySignup}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold btn-lift hover:border-trital-navy hover:bg-slate-50"
                  data-testid="shifts-nav-facility-signup"
                >
                  Post a Shift
                </Link>
                <Link
                  to={SHIFTS_CTAS.clinicianSignup}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
                  data-testid="shifts-nav-clinician-signup"
                >
                  Find Shifts <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                </Link>
              </>
            )}
          </div>

          {/* Mobile trigger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden ml-auto p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="shifts-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.8} /> : <Menu className="w-5 h-5" strokeWidth={1.8} />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="lg:hidden fixed inset-0 top-[76px] z-30 bg-white overflow-y-auto"
            data-testid="shifts-mobile-drawer"
          >
            <div className="px-5 py-6 pb-28 space-y-1">
              {NAV.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-4 rounded-xl font-display font-bold text-lg text-slate-900 hover:bg-slate-50"
                >
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-slate-200 my-4" />
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center justify-between px-4 py-3 rounded-xl text-[14px] font-semibold text-slate-600 hover:bg-slate-50">
                tritalcare.com — full site <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <div className="pt-5 mt-5 border-t border-slate-200 space-y-2.5">
                {user ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold"
                  >
                    Open dashboard <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                  </Link>
                ) : (
                  <>
                    <Link
                      to={SHIFTS_CTAS.facilitySignup}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-navy text-white font-semibold"
                    >
                      Post a Shift
                    </Link>
                    <Link
                      to={SHIFTS_CTAS.clinicianSignup}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white font-semibold"
                    >
                      Find Shifts <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                    </Link>
                    <Link
                      to={SHIFTS_CTAS.login}
                      onClick={() => setMobileOpen(false)}
                      className="block text-center px-5 py-3.5 text-[14px] text-slate-600"
                    >
                      Sign in
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page content */}
      <main>{children}</main>

      <ShiftsFooter />
    </div>
  );
}
